import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useState, useCallback, useRef, useEffect } from 'react';
import { api } from '../services/api';
import { convertObjectToFormData, convertValidationErrorToFormError } from '../utils/form';
import { ErrorOption } from 'react-hook-form';

interface UseQueryConfig<T, D> {
  runImmediate?: boolean;
  initialData?: T;
  postData?: D;
  removeDataOnRequery?: boolean;
  onQueryStart?: () => void;
  onSuccess?: (data: T) => void;
  onError?: (error: UseQueryError) => void;
}

export type UseQueryError =
  | {
      status: number;
      message: string;
      data: unknown;
      isValidationError?: false;
    }
  | {
      status: 422;
      message: string;
      data: (ErrorOption & { name: string })[];
      isValidationError: true;
    };

export interface UseQueryState<T> {
  result: T | null;
  loading: boolean;
  error: UseQueryError | null;
}

export const useQuery = <
  ResponseDataType,
  RequestDataType extends Record<string, string | number | Blob> = Record<string, string | number | Blob>
>(
  config: AxiosRequestConfig<RequestDataType>,
  {
    runImmediate = false,
    initialData,
    postData,
    removeDataOnRequery = true,
    onSuccess,
    onError,
    onQueryStart,
  }: UseQueryConfig<ResponseDataType, RequestDataType> = {}
) => {
  /**
   * Using ref to add config as current value to prevent unmount hook getting called on update
   *
   * And updating value on change
   */
  const configRef = useRef(config);
  useEffect(() => {
    configRef.current = config;
  }, [config]);
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  const onQueryStartRef = useRef(onQueryStart);
  useEffect(() => {
    onQueryStartRef.current = onQueryStart;
  }, [onQueryStart]);

  const cancelTokenSourceRef = useRef(axios.CancelToken.source());

  const isMounted = useRef(false);
  const [state, setState] = useState<UseQueryState<ResponseDataType>>({
    result: initialData || null,
    loading: false,
    error: null,
  });

  const clearErrors = () => {
    setState((prevState) => ({
      ...prevState,
      error: null,
    }));
  };

  const resetResult = () => {
    setState((prevState) => ({
      ...prevState,
      result: initialData || null,
    }));
  };

  const runQuery = useCallback(
    async (postData?: RequestDataType) => {
      if (onQueryStartRef.current) {
        onQueryStartRef.current();
      }
      // Set loading to true and error to null before fetching
      setState((prevState) => ({
        loading: true,
        error: null,
        result: removeDataOnRequery ? null : prevState.result,
      }));

      const formData = convertObjectToFormData(postData);
      try {
        cancelTokenSourceRef.current = axios.CancelToken.source();

        const response: AxiosResponse<ResponseDataType, RequestDataType> = await api({
          ...configRef.current,
          data: postData ? formData : undefined,
          cancelToken: cancelTokenSourceRef.current.token,
        });
        setState({
          error: null,
          result: response.data,
          loading: false,
        });
        if (onSuccessRef.current) {
          onSuccessRef.current(response.data);
        }
      } catch (error) {
        if (isMounted.current) {
          // Check if request was cancelled
          if (axios.isCancel(error)) {
            const error = {
              data: null,
              message: 'Request was cancelled. Please try again.',
              status: 400,
            };
            setState({
              result: null,
              loading: false,
              error,
            });
            if (onErrorRef.current) {
              onErrorRef.current(error);
            }
          } else {
            const err = error as AxiosError;

            /**
             * Handling validation error
             */
            if (err.response && err.response.status === 422) {
              const formattedError: UseQueryError = {
                status: 422,
                data: convertValidationErrorToFormError(err.response?.data.errors),
                message: err.response?.data.message || 'The given data was invalid. Please enter the valid data.',
                isValidationError: true,
              };
              setState({
                result: null,
                loading: false,
                error: formattedError,
              });
              if (onErrorRef.current) {
                onErrorRef.current(formattedError);
              }
            } else {
              /**
               * Else sent the error as sent from server
               */
              const formattedError = {
                data: err.response?.data,
                message: err.response?.data.message || 'Could not connect to server. Please try again later.',
                status: err.response?.status || 500,
              };
              setState({
                result: null,
                loading: false,
                error: formattedError,
              });
              if (onErrorRef.current) {
                onErrorRef.current(formattedError);
              }
            }
          }
        }
      }
    },
    [removeDataOnRequery]
  );

  useEffect(() => {
    isMounted.current = true;

    // Run on mounting if runImmediate is true
    if (runImmediate) {
      runQuery(postData);
    }

    return () => {
      cancelTokenSourceRef.current.cancel();
      isMounted.current = false;
    };
  }, [runQuery, postData, runImmediate]);

  return { ...state, runQuery, setState, clearErrors, resetResult, cancelQuery: cancelTokenSourceRef.current.cancel };
};
